/**
 * Request/response-shape schemas for the /staff route family.
 *
 * These used to live inline in apps/api/src/routes/staff.ts, invisible to
 * the strictness sweep (strictness.test.ts). Moved here verbatim — shapes
 * are unchanged; only the export location moved.
 */
import { z } from 'zod';
import { LangCode, UserId } from './primitives.js';

const StaffPosition = z.enum([
  'driver',
  'personal_assistant',
  'housemaid',
  'gardener',
  'nanny',
  'cook',
  'other',
]);

export const StaffRow = z.object({
  id: z.string(),
  full_name: z.string(),
  username: z.string().nullable(),
  email: z.string().nullable(),
  phone_e164: z.string().nullable(),
  preferred_lang: LangCode,
  must_change_password: z.boolean(),
  disabled_at: z.string().nullable(),
  avatar_r2_key: z.string().nullable(),
  position: StaffPosition,
  emergency_contact_name: z.string().nullable(),
  emergency_contact_phone: z.string().nullable(),
  hire_date: z.string().nullable(),
  on_trial: z.boolean(),
  capabilities: z.array(z.string()),
  created_at: z.string(),
});
export type StaffRow = z.infer<typeof StaffRow>;

export const ProvisionedStaff = z.object({
  user: StaffRow.omit({
    position: true,
    emergency_contact_name: true,
    emergency_contact_phone: true,
    hire_date: true,
    on_trial: true,
    capabilities: true,
  }),
  position: StaffPosition,
  handover: z.object({
    household_code_hint: z.literal('see /v1/household for the code'),
    username: z.string(),
    initial_password: z.string(),
  }),
});

export const ResetStaffPasswordRequest = z.object({
  new_password: z.string().min(10).max(128).optional(),
});
export const ResetStaffPasswordResponse = z.object({
  user_id: z.string(),
  new_password: z.string(),
});

/** The API's error envelope, as responses schemas declare it. */
export const ErrorEnvelope = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

/** `:id` path parameter naming a user (staff member). */
export const UserIdParams = z.object({ id: UserId });

export const StaffNamesResponse = z.object({
  items: z.array(z.object({ id: z.string(), full_name: z.string() })),
});

export const UpdateStaffWhatsAppBody = z.object({
  phone_e164: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/)
    .nullable()
    .optional(),
  whatsapp_opt_in: z.boolean().optional(),
});

export const SetStaffTrialBody = z.object({ on_trial: z.boolean() });

/**
 * What this person is DECLARED to be able to do — 'cleaning', 'pool',
 * 'car', … the domain vocabulary of apps/api/src/intelligence/domains.ts.
 * Free strings rather than an enum so a household can declare a capability
 * the taxonomy has not grown yet; the chips offer the domain ids.
 */
export const SetStaffCapabilitiesBody = z
  .object({ capabilities: z.array(z.string().min(1).max(40)).max(20) })
  .strict();

export const SetStaffPermissionsBody = z
  .object({
    can_assign_work: z.boolean().optional(),
    can_manage_staff: z.boolean().optional(),
    can_see_money: z.boolean().optional(),
  })
  // Silent unknown keys were a real footgun: a caller sending
  // `assign_work` (no `can_` prefix) got a 200 and nothing changed.
  .strict();

export const SetStaffAvatarBody = z.object({ avatar_r2_key: z.string().nullable() });
export const SetStaffAvatarResponse = z.object({
  user_id: z.string(),
  avatar_r2_key: z.string().nullable(),
});
